import { recordLookup } from "../site/lookup-visit.js";
import { hasSession } from "../site/save-word.js";

/**
 * The words a reader opened the meaning of, reported to lookup history.
 *
 * **Opening a meaning is the look-up, and the fetch behind it is not.** The
 * list asks the site about every word in a pasted text, which for an article
 * is a few hundred entries nobody chose. Recording those would fill a free
 * reader's hundred-item history from one press of Paste and evict everything
 * already in it. Pressing a row is a reader saying they wanted that word.
 *
 * A reader who follows the link to the entry instead is recorded by that page,
 * through `src/site/lookup-history.ts`. Both post the same request.
 *
 * **Nothing here decides whether history is on.** The handler checks the
 * session, the account and whether this reader has opted in, and answers a
 * reader who has not without writing anything. What this checks is only
 * whether the page has a session at all, which is what keeps a signed-out
 * visitor from posting into a 401 on every row they open.
 */

/** What this needs told, both of which a test hands its own. */
export interface SegmenterLookupsProperties {
  /** The page, which says whether there is a reader. */
  readonly document?: Document | undefined;

  /** What posts one visit. */
  readonly record?: typeof recordLookup | undefined;

  /** What makes the request. */
  readonly fetch?: typeof fetch | undefined;
}

/** One page's look-ups from the word list. */
export class SegmenterLookups {
  readonly #document: Document;
  readonly #record: typeof recordLookup;
  readonly #fetch: typeof fetch;

  /**
   * The words already reported.
   *
   * A reader opening and closing the same row is one look-up. The handler
   * would take the second as a repeat visit and move the word to the top of
   * the history, which is not what closing and reopening a cell means.
   */
  readonly #recorded = new Set<string>();

  constructor(properties: SegmenterLookupsProperties = {}) {
    this.#document = properties.document ?? document;
    this.#record = properties.record ?? recordLookup;
    this.#fetch = properties.fetch ?? globalThis.fetch;
  }

  /** Report that the reader opened a word's meaning. */
  opened(headword: string): void {
    if (this.#recorded.has(headword) || !hasSession(this.#document)) {
      return;
    }

    this.#recorded.add(headword);
    this.#record(this.#fetch, { kind: "ci", id: headword });
  }
}
